import { type SavableProperty } from "#src/composables/savable-property.composable.ts";
import {
  COUNTRIES_WITH_US,
  STATES,
} from "#src/constants/states-and-countries.ts";
import { type Address } from "#src/types/Address.ts";

import {
  mdiCalendarBlankMultiple,
  mdiCity,
  mdiEarth,
  mdiHomeGroup,
  mdiMapMarker,
  mdiSignRealEstate,
} from "@mdi/js";
import { nextTick, type Ref } from "vue";

const FOCUS_TYPE = {
  BUTTON: "BUTTON",
  BIRTHDATE: "BIRTHDATE",
  SLIDER: "SLIDER",
  DEFAULT: "DEFAULT",
  TEXTAREA: "TEXTAREA",
};

export type FocusType = (typeof FOCUS_TYPE)[keyof typeof FOCUS_TYPE];

export interface FormBuilderRow {
  fields: FormField[]; // TODO(tim) Properly type
  key: string;
  componentProps?: {
    class?: string;
    "data-testid"?: string;
    dense?: boolean;
    align?: string;
    mdColSize?: number | string;
    colSize?: number | string;
  };
}

//NOTE(tim): this is for savable properties. will need to confirm types
interface BasicFieldProps {
  dataTestid: string;
  savableProperty: SavableProperty;
  label?: string;
  executeSaveHook?: boolean;
  hideDetails?: boolean;
  onChange?: () => void;
  save?: () => Promise<unknown>;
  isOptional?: boolean;
}

// NOTE(Tim) "Key" comes from builder props
interface FieldConfig<T> {
  is: string;
  focusType: FocusType;
  savableProperty?: SavableProperty;
  componentProps: T;
}

// NOTE(tim): this needs to be updated with all the interfaces in this file
type InputFormFieldProps =
  | BasicFieldProps
  | CustomButtonsProps
  | YesNoFieldProps
  | RadioFieldProps
  | CheckboxFieldProps
  | RatingFieldProps
  | AdvancedHealthFieldProps
  | AutocompleteFieldProps
  | AddressAutofillFieldProps;

type DisplayFormFieldProps =
  | FormSubheaderProps
  | FormLoaderProps
  | FormIconProps
  | FormTextLinkProps
  | FormAlertProps
  | FormExpansionPanelsProps
  | FormExpansionPanelProps
  | FormExpansionPanelTitleProps
  | FormBuilderFieldProps;

export type FormFieldProps = InputFormFieldProps | DisplayFormFieldProps;

type SelectableItemValue = string | number | boolean | null;

type SelectableItem =
  | SelectableItemValue
  | {
      title: string;
      value: SelectableItemValue;
      dataTestid?: string;
      prependIcon?: string;
    };

interface FormBuilderFieldSlot extends FormBuilderRow {
  name: string;
}

export interface BuilderProps {
  key: string;
  mdColSize?: number | string | null;
  colSize?: number | string | null;
  class?: string;
  style?: string;
  align?: string;
  slots?: FormBuilderFieldSlot[];
}

interface FormTextLinkProps {
  textLinkParts: TextLinkParts;
}
interface TextLinkParts {
  type: string;
  text: string;
  props?: {
    href: string;
    target: string;
    class: string;
  };
}
interface AddressAutofillFieldProps extends BasicFieldProps {
  placeholder: string | null;
  autocomplete: string | null;
}

interface AddressPartProps {
  dataTestid: string;
  label: string;
  placeholder: string | null;
  savableProperty: SavableProperty;
}

interface OptionalAddressPartProps extends AddressPartProps {
  visible: boolean;
}

interface AutofillAddressPartProps extends AddressPartProps {
  autofocus: boolean;
  save: () => Promise<unknown>;
  autocomplete: string;
}

interface YearsAtAddressPartProps extends OptionalAddressPartProps {
  maxYears: number | null;
}

interface AddressFieldProps {
  switcher: {
    manualEntry: boolean;
    setManualEntry: (value: boolean) => void;
  };
  fieldOptions: {
    street: AddressPartProps;
    state: AddressPartProps;
    city: AddressPartProps;
    zip: AddressPartProps;
    autofill: AutofillAddressPartProps;
    country?: OptionalAddressPartProps;
    years_at_address?: YearsAtAddressPartProps;
  };
}

interface CustomButtonsProps extends BasicFieldProps {
  ignoreChangelessClick: boolean;
  skipValidateOnChange?: boolean;
  items: SelectableItem[];
  justify: string | null;
  ariaLabelledBy?: string | null;
}

interface YesNoFieldProps extends BasicFieldProps {
  ignoreChangelessClick: boolean;
  justify: string;
}

interface TextFieldProps extends BasicFieldProps {
  placeholder?: null | string;
  prependInnerIcon?: null | string;
  hideDetails?: boolean;
}

interface CurrencyFieldProps extends TextFieldProps {
  includeNegatives?: boolean;
  includeDecimals?: boolean;
}

interface SelectFieldProps extends TextFieldProps {
  items: SelectableItem[];
  returnObject?: boolean;
}

interface SliderFieldProps extends BasicFieldProps {
  max: number | null;
  step: number | null;
  hideDetails?: boolean;
}

interface MaskedFieldProps extends TextFieldProps {
  mask: string | null;
  inputmode: string | null;
}

interface CheckAlertFieldProps extends BasicFieldProps {
  alertProps: {
    variant: string;
    border: string;
    class: string;
    type: string;
    text: string;
  };
}
interface IntegerFieldProps extends BasicFieldProps {
  prependInnerIcon: string | null;
  placeholder: string | null;
  min?: number | null;
  max?: number | null;
  mask?: string | null;
  hideDetails?: boolean;
}

interface RadioFieldProps extends BasicFieldProps {
  items: SelectableItem[];
  centered: boolean;
  inline?: boolean;
}

interface CheckboxFieldProps extends BasicFieldProps {
  items: SelectableItem[];
}

interface RatingFieldProps extends BasicFieldProps {
  placeholder: string | null;
  prependInnerIcon: string | null;
  items: SelectableItem[];
}

interface AdvancedHealthFieldProps extends BasicFieldProps {
  onlyActiveLine: boolean;
  autofocus: boolean;
}

interface AutocompleteFieldProps extends BasicFieldProps {
  prependInnerIcon: string | null;
  placeholder: string | null;
  items: SelectableItem[];
  customFilter?: (
    value: SelectableItemValue,
    query: string,
    item: SelectableItem
  ) => number;
  otherItem?: SelectableItem;
}

interface FormSubheaderProps {
  title: string;
}

interface FormAlertProps {
  text: string;
}

interface FormLoaderProps {
  message: string;
}

interface FormExpansionPanelsProps {
  accordion: boolean;
  class: string;
  modelValue: Ref<number | null>;
  "onUpdate:modelValue": (v: number) => void;
}

interface FormExpansionPanelProps {
  eager?: boolean;
  index: number;
  "data-testid": string;
}
interface FormExpansionPanelTitleProps {
  color: string;
  class: string;
}

export interface InputField extends BuilderProps {
  is: FieldConfig<InputFormFieldProps>["is"];
  focusType: FieldConfig<InputFormFieldProps>["focusType"];
  componentProps: {
    savableProperty?: FieldConfig<InputFormFieldProps>["savableProperty"];
  } & FieldConfig<InputFormFieldProps>["componentProps"];
}

export interface DisplayField extends BuilderProps {
  is: FieldConfig<DisplayFormFieldProps>["is"];
  componentProps?: FieldConfig<DisplayFormFieldProps>["componentProps"];
}

export type FormField = InputField | DisplayField | FormBuilderFieldProps;

interface FormBuilderFieldProps {
  structure?: FormBuilderRow[];
}

export function FormBuilderField(
  key: string,
  structure: FormBuilderRow[]
): DisplayField {
  return { is: "FormBuilder", key, componentProps: { structure } };
}

export function FormFieldAdapter(
  fieldConfig: FieldConfig<FormFieldProps>,
  builderProps: BuilderProps,
  additionalProps = {}
): FormField {
  if ("savableProperty" in fieldConfig.componentProps) {
    return {
      is: fieldConfig.is,
      ...builderProps,
      focusType: fieldConfig.focusType,
      componentProps: {
        ...fieldConfig.componentProps,
        ...additionalProps,
      },
    };
  }

  return {
    is: fieldConfig.is,
    ...builderProps,
    componentProps: {
      ...fieldConfig.componentProps,
      ...additionalProps,
    },
  };
}

export function CustomButtonsField(
  componentProps: CustomButtonsProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderButtonsInput",
    focusType: FOCUS_TYPE.BUTTON,
    componentProps,
  };
}

export function YesNoField(
  componentProps: YesNoFieldProps
): FieldConfig<InputFormFieldProps> {
  return CustomButtonsField({
    ...componentProps,
    items: [
      {
        title: "Yes",
        value: true,
        dataTestid: `${componentProps.dataTestid}-yes`,
      },
      {
        title: "No",
        value: false,
        dataTestid: `${componentProps.dataTestid}-no`,
      },
    ],
  });
}

export function TextField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderTextInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function TextareaField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderTextareaInput",
    focusType: FOCUS_TYPE.TEXTAREA,
    componentProps,
  };
}

export function EmailField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderEmailInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function DateField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderDateInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function CurrencyField(
  componentProps: CurrencyFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderCurrencyInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function SliderField(
  componentProps: SliderFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderSliderInput",
    focusType: FOCUS_TYPE.SLIDER,
    componentProps,
  };
}

export function BirthdateField(
  componentProps: BasicFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderBirthdateInput",
    focusType: FOCUS_TYPE.BIRTHDATE,
    componentProps,
  };
}

export function SsnField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderSsnInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function MaskedField(
  componentProps: MaskedFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderMaskedInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function TinField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return MaskedField({
    ...componentProps,
    mask: "##-#######",
    inputmode: "numeric",
  });
}

export function PhoneField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return MaskedField({
    ...componentProps,
    mask: "(###) ###-####",
    inputmode: "tel",
  });
}

export function RoutingNumberField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return MaskedField({
    ...componentProps,
    mask: "#########",
    placeholder: "#########",
    inputmode: "numeric",
  });
}

export function AccountNumberField(
  componentProps: TextFieldProps
): FieldConfig<InputFormFieldProps> {
  return MaskedField({
    ...componentProps,
    mask: "#################",
    placeholder: "#################",
    inputmode: "numeric",
  });
}

export function CheckAlertField(
  componentProps: CheckAlertFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderCheckAlertInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function IntegerField(
  componentProps: IntegerFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderIntegerInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function SelectField(
  componentProps: SelectFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderSelectInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function RadioField(
  componentProps: RadioFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderRadioInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function CheckboxField(
  componentProps: CheckboxFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderCheckboxGroup",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function RatingField(
  componentProps: RatingFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderRatingInput",
    focusType: FOCUS_TYPE.BUTTON,
    componentProps,
  };
}

export function AdvancedHealthField(
  componentProps: AdvancedHealthFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderAdvancedHealthInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function AutocompleteField(
  componentProps: AutocompleteFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderAutocompleteInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function AddressAutofillField(
  componentProps: AddressAutofillFieldProps
): FieldConfig<InputFormFieldProps> {
  return {
    is: "FormBuilderAddressAutofillInput",
    focusType: FOCUS_TYPE.DEFAULT,
    componentProps,
  };
}

export function FormSubheader(
  title: string,
  key: string,
  componentProps: object = {}
): DisplayField {
  return {
    is: "FormBuilderSubheader",
    colSize: 12,
    mdColSize: 12,
    key,
    componentProps: {
      title,
      ...componentProps,
    },
  };
}

export function FormBasicSubheader(
  title: string,
  key: string,
  componentProps: Partial<BuilderProps> = {}
): DisplayField {
  return {
    is: "FormBuilderBasicSubheader",
    colSize: 12,
    mdColSize: componentProps?.mdColSize || 12,
    key,
    componentProps: {
      title,
      ...componentProps,
    },
  };
}

export function FormLoader(
  message: string,
  key: string,
  componentProps = {}
): DisplayField {
  return {
    is: "Loader",
    colSize: 12,
    mdColSize: 12,
    key,
    componentProps: {
      message,
      ...componentProps,
    },
  };
}

interface FormIconProps {
  icon: string;
}

export function FormIcon(
  icon: string,
  key: string,
  componentProps: {
    color: string | null;
    mdColSize?: number | string;
    colSize?: number | string;
  } = { color: null }
): DisplayField {
  return {
    is: "VIcon",
    mdColSize: componentProps?.mdColSize || "auto",
    colSize: componentProps?.colSize || "auto",
    componentProps: {
      icon,
      ...componentProps,
    },
    key,
  };
}

export function FormSpacer(key: string) {
  return {
    is: "VSpacer",
    key,
  };
}

export function FormTextLink(
  textLinkParts: TextLinkParts,
  key: string,
  componentProps = {}
): DisplayField {
  return {
    is: "FormBuilderTextLink",
    key,
    mdColSize: 12,
    colSize: 12,
    componentProps: {
      textLinkParts,
      ...componentProps,
    },
  };
}

export function FormAlert(
  text: string,
  key: string,
  componentProps = {}
): DisplayField {
  return {
    is: "VAlert",
    colSize: 12,
    mdColSize: 12,
    key,
    componentProps: {
      text,
      ...componentProps,
    },
  };
}

export function FormButton(
  text: string,
  key: string,
  componentProps: Partial<BuilderProps> &
    Partial<{
      prependIcon: string;
      icon: string;
      onClick: () => void;
      variant: string;
      rounded: boolean;
      color: string;
      size: string;
    }>
): DisplayField {
  return {
    is: "VBtn",
    colSize: componentProps?.colSize || 12,
    mdColSize: componentProps?.mdColSize || 12,
    key,
    componentProps: {
      text,
      ...componentProps,
    },
  };
}

export function FormExpansionPanels(
  key: string,
  fields: FormField[],
  componentProps: FormExpansionPanelsProps
): DisplayField {
  return {
    is: "VExpansionPanels",
    colSize: 12,
    mdColSize: 12,
    key,
    slots: [{ name: "default", key: "default", fields }],
    componentProps,
  };
}

export function FormExpansionPanel(
  key: string,
  fields: FormField[],
  componentProps: FormExpansionPanelProps
): DisplayField {
  return {
    is: "VExpansionPanel",
    colSize: 12,
    mdColSize: 12,
    key,
    slots: [{ name: "default", key: "default", fields }],
    componentProps: {
      ...componentProps,
      eager: true,
    },
  };
}

export function FormExpansionPanelBody(
  key: string,
  structure: FormBuilderRow[]
): DisplayField {
  return {
    is: "VExpansionPanelText",
    colSize: 12,
    mdColSize: 12,
    key: "body",
    slots: [
      {
        name: "default",
        key: "default",
        fields: [
          {
            ...FormBuilderField(key + "base", structure),
            key,
          },
        ],
      },
    ],
  };
}

export function FormExpansionPanelTitle(
  key: string,
  fields: FormField[],
  componentProps: FormExpansionPanelTitleProps
): FormField {
  return {
    is: "VExpansionPanelTitle",
    colSize: 12,
    mdColSize: 12,
    key,
    slots: [{ name: "default", key: "default", fields }],
    componentProps,
  };
}

export function AddressField(args: AddressFieldProps): FormField[] {
  const { switcher, fieldOptions } = args;
  const fields = [];
  if (fieldOptions.street.savableProperty.model || switcher.manualEntry) {
    switcher.setManualEntry(true);
    const mdColSize =
      fieldOptions?.country?.visible || fieldOptions?.years_at_address?.visible
        ? 3
        : 4;
    fields.push(
      FormFieldAdapter(
        TextField({
          prependInnerIcon: mdiSignRealEstate,
          ...fieldOptions.street,
        }),
        { key: "street", mdColSize: 12, colSize: 12 }
      ),
      FormFieldAdapter(
        TextField({
          prependInnerIcon: mdiCity,
          ...fieldOptions.city,
        }),
        { key: "city", mdColSize, colSize: 12 }
      ),
      FormFieldAdapter(
        AutocompleteField({
          prependInnerIcon: mdiHomeGroup,
          items: STATES,
          ...fieldOptions.state,
        }),
        { key: "state", mdColSize, colSize: 12 }
      ),
      FormFieldAdapter(
        MaskedField({
          prependInnerIcon: mdiMapMarker,
          inputmode: "numeric",
          mask: "#####",
          ...fieldOptions.zip,
        }),
        { key: "zip", mdColSize, colSize: 12 }
      )
    );

    if (fieldOptions?.country?.visible) {
      fields.push(
        FormFieldAdapter(
          AutocompleteField({
            prependInnerIcon: mdiEarth,
            items: COUNTRIES_WITH_US,
            ...fieldOptions.country,
          }),
          { key: "country", mdColSize, colSize: 12 }
        )
      );
    }

    if (fieldOptions?.years_at_address?.visible) {
      fields.push(
        FormFieldAdapter(
          IntegerField({
            prependInnerIcon: mdiCalendarBlankMultiple,
            ...fieldOptions.years_at_address,
          }),
          { key: "years-at-address", mdColSize }
        )
      );
    }
  } else {
    fields.push(
      FormFieldAdapter(
        AddressAutofillField({
          label: fieldOptions.autofill.label,
          placeholder: fieldOptions.autofill.placeholder,
          dataTestid: fieldOptions.autofill.dataTestid,
          savableProperty: fieldOptions.autofill.savableProperty,
          autocomplete: fieldOptions.autofill.autocomplete,
        }),
        {
          colSize: 12,
          mdColSize: 12,
          key: fieldOptions.autofill.dataTestid,
        },
        {
          autofocus: fieldOptions.autofill.autofocus,
          loading: Boolean(fieldOptions.street.savableProperty.requests.length),
          disabled: Boolean(
            fieldOptions.street.savableProperty.requests.length
          ),
          onError: () => switcher.setManualEntry(true),
          onSwitchToManual: () => switcher.setManualEntry(true),
          "onUpdate:modelValue": (address: Address) => {
            fieldOptions.street.savableProperty.load(address.street_address);
            fieldOptions.state.savableProperty.load(address.state);
            fieldOptions.city.savableProperty.load(address.city);
            fieldOptions.zip.savableProperty.load(address.zip);
            if (fieldOptions.country?.savableProperty) {
              fieldOptions.country.savableProperty.load(address.country);
            }
          },
          save: async () => {
            await fieldOptions.autofill.save();
            switcher.setManualEntry(true);
          },
        }
      )
    );
  }

  return fields;
}

type StructureElements =
  | FormField
  | FormBuilderRow
  | FormBuilderRow[]
  | FormBuilderFieldSlot;

interface StructureData {
  field: InputField;
  parents: StructureElements[];
}
function getAllStructureCb(
  accumulator: StructureData[],
  field: StructureElements,
  parents: StructureElements[] = []
) {
  const newParents: StructureElements[] = [...parents, field];

  if (Array.isArray(field)) {
    return field.forEach(s => getAllStructureCb(accumulator, s, newParents));
  }

  if ("slots" in field) {
    field.slots?.forEach(slot =>
      getAllStructureCb(accumulator, slot, newParents)
    );
  }

  if ("fields" in field) {
    field.fields.forEach(s => getAllStructureCb(accumulator, s, newParents));
  }

  if ("componentProps" in field && field.componentProps) {
    if ("structure" in field.componentProps && field.componentProps.structure) {
      field.componentProps.structure.forEach(s =>
        getAllStructureCb(accumulator, s, newParents)
      );
    }

    if (
      "savableProperty" in field.componentProps &&
      field.componentProps?.savableProperty
    ) {
      accumulator.push({ field: field as InputField, parents });
    }
  }
}

function getAllStructure(structure: FormBuilderRow[]): StructureData[] {
  const acc: StructureData[] = [];
  getAllStructureCb(acc, structure);
  return acc;
}

interface FillableFormStructure {
  property: SavableProperty;
  formField: InputField;
  key: string;
  parents?: StructureElements[];
  optional: boolean;
}

function getFillableFormStructure(
  structure: FormBuilderRow[]
): FillableFormStructure[] {
  return getAllStructure(structure).reduce((acc, args) => {
    acc.push({
      property: args.field.componentProps.savableProperty,
      formField: args.field,
      key: args.field.key,
      parents: args.parents,
      optional: Boolean(
        "isOptional" in args.field.componentProps &&
          args.field.componentProps.isOptional
      ),
    });

    return acc;
  }, [] as FillableFormStructure[]);
}

interface ValdiatedFormStructure extends FillableFormStructure {
  valid: boolean;
}

export function validateFormStructure(
  structure: FormBuilderRow[],
  throwOnFailure: boolean = false
): boolean {
  const failures = getFillableFormStructure(structure).reduce((acc, item) => {
    const field = item.property;
    const noPendingRequests = !field.requests.length;

    if (item.optional) {
      const isNotDefined = !field.model && field.model !== 0;
      if (isNotDefined && noPendingRequests) return acc;
    }

    const valid = field.validate(true);

    if (valid) return acc;

    acc.push({ valid, ...item });

    return acc;
  }, [] as ValdiatedFormStructure[]);

  if (failures.length && throwOnFailure) {
    throw failures;
  }
  return !failures.length;
}

export async function focusOnFirstElement(structure: FormBuilderRow[]) {
  await nextTick();
  await nextTick();
  const focusable = getFillableFormStructure(structure);
  focusable.find(args => setFocusOnField(args, { isEmpty: false }));
}

export async function focusOnFirstEmptyElement(
  structure: FormBuilderRow[],
  previousFieldList: string[] = [],
  behavingOnChange = false
) {
  await nextTick();
  await nextTick();
  const focusable = getFillableFormStructure(structure);

  const previousFields = new Set<string>(previousFieldList);
  const newFields: FillableFormStructure[] = [];
  const currentFields: string[] = [];

  focusable.forEach(data => {
    currentFields.push(data.key);
    if (previousFields.has(data.key)) return;
    newFields.push(data);
  });

  const focusStruct = behavingOnChange ? newFields : focusable;
  focusStruct.find(data => {
    return setFocusOnField(data, { isEmpty: true, ignoreOptional: true });
  });

  return currentFields;
}

function setFocusOnField(
  field: FillableFormStructure,
  { isEmpty = false, ignoreOptional = false } = {}
) {
  if (field.optional && ignoreOptional) {
    return false;
  }

  if (!field.formField.componentProps.savableProperty) {
    return false;
  }

  if (isEmpty) {
    if (field.formField.componentProps.savableProperty.model !== null) {
      return false;
    }
  }

  let el;

  if (field.formField.focusType === FOCUS_TYPE.BUTTON) {
    const parent = document.getElementById(
      `${field.formField.componentProps.dataTestid}-group`
    );
    if (parent) el = parent.querySelector(`button[tabindex="0"]`);
  } else if (field.formField.focusType === FOCUS_TYPE.BIRTHDATE) {
    el = document.querySelector('[data-testid="birthdate-month"] input');
  } else if (field.formField.focusType === FOCUS_TYPE.SLIDER) {
    el = document.querySelector(
      `[data-testid="${field.formField.componentProps.dataTestid}"] .v-slider-thumb`
    );
  } else if (field.formField.focusType === FOCUS_TYPE.TEXTAREA) {
    el = document.querySelector(
      `[data-testid="${field.formField.componentProps.dataTestid}"] textarea`
    );
  } else {
    el = document.querySelector(
      `[data-testid="${field.formField.componentProps.dataTestid}"] input`
    );
  }

  try {
    if (
      document.activeElement &&
      ["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)
    )
      return false;
    if (el && "focus" in el && typeof el.focus === "function") el.focus();
    // eslint-disable-next-line no-unused-vars
  } catch (e) {
    return false;
  }

  return Boolean(el);
}
